import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, Card, CardContent, Table, TableBody, TableRow, TableCell, Chip, Button, Alert,
  Stack, LinearProgress, FormControlLabel, Switch, Tooltip,
} from '@mui/material';
import { PlayArrow, ContentCopy, CheckCircle, Warning, Error as ErrorIcon, Info } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import api from '../../services/api';

// Server health check (read-only). L2 engineers run it; if anything is amber or red they call the
// super admin, who takes the corrective action. Nothing on this page can change the server.

type Status = 'ok' | 'warn' | 'crit' | 'info';
const META: Record<Status, { label: string; color: 'success' | 'warning' | 'error' | 'default'; icon: React.ReactElement }> = {
  ok:   { label: 'OK',       color: 'success', icon: <CheckCircle fontSize="small" /> },
  warn: { label: 'Warning',  color: 'warning', icon: <Warning fontSize="small" /> },
  crit: { label: 'Critical', color: 'error',   icon: <ErrorIcon fontSize="small" /> },
  info: { label: 'Info',     color: 'default', icon: <Info fontSize="small" /> },
};
const ORDER = ['System', 'Services', 'Containers', 'Application', 'SIP & Media', 'Database', 'Call Traffic', 'Billing Integrity', 'Backups', 'Security'];
const fmtTime = (s?: string) => (s ? new Date(s).toLocaleString('en-GB', { timeZone: 'UTC' }) + ' UTC' : '—');

export const HealthCheckPage: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [auto, setAuto] = useState(false);

  const runCheck = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const { data: d } = await api.get('/health-check', { timeout: 60000 });
      setData(d);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Health check could not run. Call the super admin.');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { runCheck(); }, [runCheck]);
  useEffect(() => {
    if (!auto) return undefined;
    const t = setInterval(runCheck, 60000);
    return () => clearInterval(t);
  }, [auto, runCheck]);

  const groups = useMemo(() => {
    const g: Record<string, any[]> = {};
    for (const c of data?.checks || []) (g[c.category] = g[c.category] || []).push(c);
    return ORDER.filter((k) => g[k]).map((k) => ({ name: k, checks: g[k] }))
      .concat(Object.keys(g).filter((k) => !ORDER.includes(k)).map((k) => ({ name: k, checks: g[k] })));
  }, [data]);

  const problems = (data?.checks || []).filter((c: any) => c.status === 'crit' || c.status === 'warn');

  const copyReport = async () => {
    if (!data) return;
    const lines = [
      `esbc2 health check — ${fmtTime(data.checked_at)} — ${META[data.overall as Status].label.toUpperCase()}`,
      `${data.summary.crit} critical, ${data.summary.warn} warning, ${data.summary.ok} OK (${data.summary.total} checks)`,
      '',
      ...(problems.length ? problems.map((c: any) => `[${META[c.status as Status].label}] ${c.category} — ${c.name}: ${c.value}${c.detail ? ` (${c.detail})` : ''}`) : ['No problems found.']),
    ];
    try { await navigator.clipboard.writeText(lines.join('\n')); enqueueSnackbar('Report copied — paste it to the super admin', { variant: 'success' }); }
    catch { enqueueSnackbar('Copy failed — select the text manually', { variant: 'error' }); }
  };

  const overall: Status = data?.overall || 'info';

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} sx={{ mb: 2 }}>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h5" fontWeight={700}>Server Health Check</Typography>
          <Typography variant="body2" color="text.secondary">
            Read-only checks of the esbc2 server, services, calls, billing integrity and backups. Nothing here changes the server.
          </Typography>
        </Box>
        <FormControlLabel control={<Switch checked={auto} onChange={(e) => setAuto(e.target.checked)} />} label="Auto-refresh (1 min)" />
        <Button variant="outlined" startIcon={<ContentCopy />} disabled={!data} onClick={copyReport}>Copy report</Button>
        <Button variant="contained" startIcon={<PlayArrow />} disabled={loading} onClick={runCheck}>Run health check</Button>
      </Stack>

      {loading ? <LinearProgress sx={{ mb: 2 }} /> : null}
      {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}

      {data ? (
        <>
          <Alert
            severity={overall === 'crit' ? 'error' : overall === 'warn' ? 'warning' : 'success'}
            sx={{ mb: 2, '& .MuiAlert-message': { width: '100%' } }}
          >
            <Typography fontWeight={700}>
              {overall === 'crit' ? 'CRITICAL — action needed' : overall === 'warn' ? 'Warnings found' : 'All systems healthy'}
            </Typography>
            <Typography variant="body2">
              {data.summary.crit} critical · {data.summary.warn} warning · {data.summary.ok} OK of {data.summary.total} checks ·
              checked {fmtTime(data.checked_at)}{data.cached ? ' (cached result, under 30 s old)' : ''}
            </Typography>
            {overall !== 'ok' ? (
              <Typography variant="body2" sx={{ mt: 1 }}>
                <b>Call the Super Admin</b> to review and take corrective action. Use <b>Copy report</b> to send them the findings.
                Do not attempt to fix configuration yourself.
              </Typography>
            ) : null}
          </Alert>

          <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' } }}>
            {groups.map((g) => {
              const worst: Status = g.checks.some((c: any) => c.status === 'crit') ? 'crit'
                : g.checks.some((c: any) => c.status === 'warn') ? 'warn' : 'ok';
              return (
                <Card key={g.name} variant="outlined">
                  <CardContent sx={{ pb: '8px !important' }}>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                      <Typography variant="subtitle1" fontWeight={700} sx={{ flexGrow: 1 }}>{g.name}</Typography>
                      <Chip size="small" icon={META[worst].icon} label={META[worst].label} color={META[worst].color} />
                    </Stack>
                    <Box sx={{ overflowX: 'auto' }}>
                      <Table size="small">
                        <TableBody>
                          {g.checks.map((c: any) => (
                            <TableRow key={c.name}>
                              <TableCell sx={{ width: 110, pl: 0 }}>
                                <Chip size="small" icon={META[c.status as Status].icon} label={META[c.status as Status].label}
                                  color={META[c.status as Status].color} variant={c.status === 'info' ? 'outlined' : 'filled'} />
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" fontWeight={600}>{c.name}</Typography>
                                {c.detail ? <Typography variant="caption" color="text.secondary">{c.detail}</Typography> : null}
                              </TableCell>
                              <TableCell align="right" sx={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                                <Tooltip title={c.detail || ''}><span>{c.value}</span></Tooltip>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </Box>
                  </CardContent>
                </Card>
              );
            })}
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
            Host {data.host} · checks took {(data.duration_ms / 1000).toFixed(1)} s · every run is recorded in the audit log.
          </Typography>
        </>
      ) : null}
    </Box>
  );
};

export default HealthCheckPage;
